import React, { useEffect, useState } from "react";
import { usePayment } from "@/hooks/usePayment";
import { useToast } from "@/hooks/use-toast";
import { calculatePaymentTotals } from "@/utils/payment/paymentCalculations";
import PaymentSummary from "@/components/payment/PaymentSummary";
import PaymentCardSection from "@/components/payment/PaymentCardSection";
import { fetchBusinessInfo } from "@/services/businesInfo";
import PaymentBusinessInfo from "./PaymentBusinessInfo";

interface PaymentFormProps {
  product: any;
  additionalProducts?: any[];
  country?: string;
  region?: string;
  grossCalculation?: any;
  checkoutStripeAccountId?: string;
  externalUserId?: string;
  externalProductId?: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  product,
  additionalProducts = [],
  country = "US",
  region = "CA",
  grossCalculation,
  checkoutStripeAccountId,
  externalUserId,
  externalProductId,
}) => {
  const { toast } = useToast();
  const [businessInfo, setBusinessInfo] = useState<{
    business_name?: string;
    business_address?: string;
    street_address?: string;
    city?: string;
    state?: string;
    zip_code?: string;
  }>({});

  const allProducts = [product, ...additionalProducts];

  useEffect(() => {
    const fetchBusinessInformation = async () => {
      if (!product?.user_id) return;

      try {
        const businessInfo = await fetchBusinessInfo(product.user_id);

        if (!businessInfo) {
          console.error('Failed to fetch business info');
          return;
        }

        const businessAddress = [businessInfo.street_address, businessInfo.city, businessInfo.state, businessInfo.zip_code]
          .filter(Boolean)
          .join(', ');

        setBusinessInfo({
          business_name: businessInfo.business_name || null,
          business_address: businessAddress || null,
          street_address: businessInfo.street_address || null,
          city: businessInfo.city || null,
          state: businessInfo.state || null,
          zip_code: businessInfo.zip_code || null
        });
      } catch (error) {
        console.error('Error fetching business info:', error);
      }
    };

    fetchBusinessInformation();
  }, [product?.user_id]);

  // Create a default gross calculation using centralized logic
  const defaultCalculation = calculatePaymentTotals(allProducts, "card");
  const finalGrossCalculation = grossCalculation || {
    grossAmount: defaultCalculation.total,
    netAmount: defaultCalculation.subtotal,
    stripeFee: defaultCalculation.processingFee,
    keepMoreFee: 0,
    applicationFee: 0,
    totalFees: defaultCalculation.processingFee,
    raw: {
      grossAmountCents: Math.round(defaultCalculation.total * 100),
      netAmountCents: Math.round(defaultCalculation.subtotal * 100),
      stripeFeeCents: Math.round(defaultCalculation.processingFee * 100),
      keepMoreFeeCents: 0,
      applicationFeeCents: 0,
    },
  };

  const {
    selectedPaymentMethod,
    processingPayment,
    paymentInitialized,
    paymentError,
    handlePaymentSelect,
    initializePayment,
    clientSecret,
    connectedAccountId,
    hasMixedPaymentTypes,
  } = usePayment(
    product,
    country,
    region,
    additionalProducts,
    finalGrossCalculation,
    checkoutStripeAccountId,
    externalUserId,
    externalProductId
  );

  useEffect(() => {
    if (paymentError) {
      toast({
        title: "Payment Error",
        description: paymentError,
        variant: "destructive",
      });
    }
  }, [paymentError, toast]);

  return (
    <div className="flex items-center justify-center">
      <form
        onSubmit={(e) => e.preventDefault()}
        className="space-y-4 w-full max-w-3xl mx-auto"
      >

        {(businessInfo.business_name || businessInfo.business_address) && (
          <PaymentBusinessInfo businessInfo={businessInfo} />
        )}

        <PaymentSummary
          product={product}
          additionalProducts={additionalProducts}
          selectedPaymentMethod={selectedPaymentMethod}
        />

        <PaymentCardSection
          product={product}
          additionalProducts={additionalProducts}
          selectedPaymentMethod={selectedPaymentMethod}
          clientSecret={clientSecret}
          paymentInitialized={paymentInitialized}
          processingPayment={processingPayment}
          paymentError={paymentError}
          handlePaymentSelect={handlePaymentSelect}
          initializePayment={initializePayment}
          connectedAccountId={connectedAccountId}
          grossCalculation={finalGrossCalculation}
          hasMixedPaymentTypes={hasMixedPaymentTypes}
          checkoutStripeAccountId={checkoutStripeAccountId}
        />
      </form>
    </div>
  );
};

export default PaymentForm;